import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import { connectSupportSocket } from "../utils/supportWs";
import PageHero from "./child/PageHero";
import SupportIssueChat from "./SupportIssueChat";
import "./SupportPingLayer.css";

const SupportPingLayer = () => {
  const [issues, setIssues] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subject, setSubject] = useState("");
  const [firstMsg, setFirstMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, cls = "success") => {
    setToast({ message, class: cls });
    setTimeout(() => setToast(null), 2500);
  };

  const loadIssues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall({ url: "support/issues", method: "GET" });
      if (res?.success) {
        const list = Array.isArray(res.data) ? res.data : [];
        setIssues(list);
        setSelected((prev) => {
          if (!prev) return list[0] || null;
          return list.find((i) => String(i.id) === String(prev.id)) || prev;
        });
      }
    } catch (err) {
      showToast(err.message || "Failed to load issues", "danger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Support";
    loadIssues();
    return connectSupportSocket({ role: "user" });
  }, [loadIssues]);

  useEffect(() => {
    const onWs = (e) => {
      const d = e.detail;
      if (!d) return;
      if (d.issue) {
        setIssues((list) => {
          const rest = list.filter((i) => String(i.id) !== String(d.issue.id));
          return [d.issue, ...rest];
        });
        setSelected((prev) =>
          prev && String(prev.id) === String(d.issue.id) ? { ...prev, ...d.issue } : prev
        );
      }
    };
    window.addEventListener("support-ws", onWs);
    return () => window.removeEventListener("support-ws", onWs);
  }, []);

  const createIssue = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      showToast("Subject is required", "danger");
      return;
    }
    setCreating(true);
    try {
      const res = await apiCall({
        url: "support/issues",
        method: "POST",
        body: { subject: subject.trim(), message: firstMsg.trim() },
      });
      if (res?.success) {
        showToast("Issue created");
        setSubject("");
        setFirstMsg("");
        setShowNew(false);
        await loadIssues();
        if (res.data?.issue) setSelected(res.data.issue);
      } else {
        showToast(res?.msg || "Could not create issue", "danger");
      }
    } catch (err) {
      showToast(err.message || "Server error", "danger");
    } finally {
      setCreating(false);
    }
  };

  const openCount = issues.filter((i) => i.status !== "closed").length;

  return (
    <div className="support-ping-wrap">
      {toast ? (
        <div className={`alert alert-${toast.class} support-ping-toast`}>{toast.message}</div>
      ) : null}

      <PageHero
        title="Support"
        subtitle="Create an issue and chat live with our team — text, files, or voice."
        badge={{ icon: "mdi:lifebuoy", label: `${openCount} open` }}
        action={
          <button type="button" className="btn btn-success btn-sm" onClick={() => setShowNew(true)}>
            <Icon icon="mdi:plus" width={16} className="me-1" />
            New issue
          </button>
        }
      />

      {showNew ? (
        <div className="card support-ping-card mb-3">
          <div className="card-body">
            <h6 className="support-ping-card-title">
              <Icon icon="mdi:ticket-outline" width={20} />
              New support issue
            </h6>
            <form onSubmit={createIssue}>
              <label className="form-label">Subject</label>
              <input
                className="form-control mb-2"
                maxLength={255}
                placeholder="e.g. Session disconnects every hour"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
              <label className="form-label">First message (optional)</label>
              <textarea
                className="form-control mb-2"
                rows={3}
                placeholder="Describe the problem…"
                value={firstMsg}
                onChange={(e) => setFirstMsg(e.target.value)}
              />
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-success btn-sm" disabled={creating}>
                  {creating ? "Creating…" : "Create & open chat"}
                </button>
                <button type="button" className="btn btn-light btn-sm" onClick={() => setShowNew(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="card support-ping-card">
            <div className="card-body p-0">
              <div className="support-issue-list-head">Your issues</div>
              {loading ? (
                <p className="p-3 text-secondary-light mb-0">Loading…</p>
              ) : issues.length === 0 ? (
                <div className="support-ping-empty">
                  <Icon icon="mdi:inbox-outline" width={36} />
                  <p>No issues yet. Create one to start chatting.</p>
                </div>
              ) : (
                <ul className="support-issue-list">
                  {issues.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`support-issue-item${
                          selected && String(selected.id) === String(item.id) ? " active" : ""
                        }`}
                        onClick={() => setSelected(item)}
                      >
                        <div className="d-flex justify-content-between gap-2">
                          <strong>{item.subject}</strong>
                          {Number(item.unread_user) > 0 ? (
                            <span className="support-unread">{item.unread_user}</span>
                          ) : null}
                        </div>
                        <span className="support-issue-preview">{item.last_message || "No messages"}</span>
                        <span className={`support-ping-status ${item.status === "closed" ? "" : "ok"}`}>
                          {item.status}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-8">
          <SupportIssueChat
            role="user"
            apiBase="support"
            issue={selected}
            onIssueUpdated={(issue) => {
              setSelected((prev) => (prev && String(prev.id) === String(issue.id) ? { ...prev, ...issue } : prev));
              setIssues((list) => {
                const rest = list.filter((i) => String(i.id) !== String(issue.id));
                return [{ ...list.find((i) => String(i.id) === String(issue.id)), ...issue }, ...rest].filter(
                  Boolean
                );
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SupportPingLayer;
